import {
  consumeAuthSession,
  getStoredAuth,
  resolveApiDomain,
  resolveAuthAppUrl,
  type StoredAuth,
} from "nazare-ui/utils";

const AUTH_STORAGE_KEY = "nazare-session";

type AuthResponse = Required<Pick<StoredAuth, "accessToken" | "refreshToken" | "expiresAt">> & {
  user: unknown;
};

const REFRESH_MUTATION = `
  mutation RefreshToken($token: String!) {
    refreshToken(refreshRequestInput: { refreshToken: $token }) {
      accessToken
      refreshToken
      expiresAt
      user
    }
  }
`;

function normaliseExpiry(value: number): number {
  return value < 1e12 ? value * 1000 : value;
}

function hasUsableAccessToken(auth: StoredAuth | null): boolean {
  return Boolean(auth?.accessToken && (!auth.expiresAt || normaliseExpiry(auth.expiresAt) > Date.now()));
}

function storeAuth(auth: AuthResponse): void {
  window.localStorage.setItem(
    AUTH_STORAGE_KEY,
    JSON.stringify({ ...auth, expiresAt: normaliseExpiry(auth.expiresAt) }),
  );
}

async function mutateAuth(
  query: string,
  variables: Record<string, string>,
  operation: "refreshToken",
  bearer?: string,
): Promise<AuthResponse> {
  const apiDomain = resolveApiDomain(window.location.origin, import.meta.env.VITE_NAZARE_API_DOMAIN);
  const response = await fetch(`${apiDomain}/graphql`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`Authentication failed with status ${response.status}.`);

  const payload: {
    data?: Partial<Record<typeof operation, Partial<AuthResponse> | null>>;
    errors?: unknown[];
  } = await response.json();
  const auth = payload.data?.[operation];
  if (!auth?.accessToken || !auth.refreshToken || typeof auth.expiresAt !== "number") {
    throw new Error("Authentication did not return a complete session.");
  }

  return auth as AuthResponse;
}

async function ensureValidSession(): Promise<boolean> {
  const current = getStoredAuth();
  if (hasUsableAccessToken(current)) return true;
  if (!current?.refreshToken) return false;

  try {
    storeAuth(
      await mutateAuth(REFRESH_MUTATION, { token: current.refreshToken }, "refreshToken", current.refreshToken),
    );
    return true;
  } catch {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return false;
  }
}

export async function preparePlatformAuth(): Promise<boolean> {
  const session = consumeAuthSession(window.location, window.history);
  if (session) {
    storeAuth(session);
    return true;
  }

  if (await ensureValidSession()) {
    return true;
  }

  const authUrl = new URL(resolveAuthAppUrl(window.location.href, import.meta.env.VITE_NAZARE_AUTH_APP_URL));
  authUrl.searchParams.set("return_to", window.location.href);
  window.location.replace(authUrl);
  return false;
}
